/* Form Component to Add or Edit Customer details*/

import { useContext, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Row,
  Col,
  Space,
  InputNumber,
} from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TDropdownOptions, TResponseModel } from "../../../types/config";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  TCustomer,
  TServicePartnerRates,
  TServicePartner,
  TProduct,
} from "../../../types";
import {
  AddEditCustomer,
  AddEditCustomerAMCRate,
  AddEditServicePartnerRate,
  GetProductList,
  GetServicePartnerList,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { UserContext } from "../../../context";
import { requiredFieldValidator } from "../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import CustomDropdown from "../../../components/custom-dropdown/custom-dropdown";
import "../../../index.css";
import { useWatch } from "antd/es/form/Form";

type DrawerProps = {
  id: number;
  partnerProductRate: TServicePartnerRates;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

export default function PartnerProductRateForm(props: DrawerProps) {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const context: any = useContext(UserContext);
  const [form] = Form.useForm();
  const gyroValue = useWatch("gyro", form);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [partnerOptions, setPartnerOptions] = useState<TDropdownOptions[]>([]);

  useEffect(() => {
    getPartnerList();
    if (props.partnerProductRate?.id) {
      form.setFieldsValue({ ...props?.partnerProductRate });
    }
  }, []);

  const getPartnerList = async () => {
    try {
      const payload = {
        data: {
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetServicePartnerList,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const mappedOption = Utils.mapDropdownOptions(data);
          setPartnerOptions(mappedOption);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  // Handle on submit event
  const onFinish = async (values: TServicePartnerRates) => {
    try {
      const { id, ...restValues } = values;
      const payload = {
        data: {
          id: props.partnerProductRate?.id ? props.partnerProductRate?.id : 0,
          ...restValues,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditServicePartnerRate, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        props.onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props?.onCloseDrawer();
        props?.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a Partner Product Rate`}
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="servicePartnerId"
                label="Service Partner"
                rules={[
                  {
                    required: true,
                    message: "Please select service partner",
                  },
                ]}
              >
                <Select
                  disabled={props?.id ? true : false}
                  placeholder="Select service partner"
                  options={partnerOptions}
                ></Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="productId"
                label="Product"
                rules={[{ required: true, message: "Please choose product" }]}
              >
                <CustomDropdown
                  isDisabled={props?.id ? true : false}
                  endPoint={GetProductList}
                  value={form.getFieldValue("productId")}
                  selectedName={form.getFieldValue("productName")}
                  onChange={(selected: TProduct) => {
                    form.setFieldValue("productId", selected.value);
                    form.setFieldValue("productName", selected.label);
                    form.setFieldValue("productCode", selected.productCode);
                  }}
                  optionId="id"
                  optionName="productName"
                  label=""
                  placeHolder="Select model"
                ></CustomDropdown>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="productCode" label="Product Code">
                <Input placeholder="Enter product code" readOnly />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="gyro"
                label="Gyro"
                rules={[
                  { required: true, message: "Please select Gyro status" },
                ]}
              >
                <Select
                  placeholder="Select Gyro status"
                  options={[
                    { label: "Yes", value: 1 },
                    { label: "No", value: 0 },
                  ]}
                />
              </Form.Item>
            </Col>

            {gyroValue === 1 && (
              <Col span={24}>
                <Form.Item
                  name="machineTypeId"
                  label="Model"
                  rules={[{ required: true, message: "Please choose a model" }]}
                >
                  <CustomDropdown
                    endPoint={GetProductList}
                    value={form.getFieldValue("machineTypeId")}
                    selectedName={form.getFieldValue("machineTypeName")}
                    onChange={(selected: TProduct) => {
                      form.setFieldsValue({
                        machineTypeId: selected.value,
                        machineTypeName: selected.label,
                      });
                    }}
                    optionId="id"
                    optionName="productName"
                    placeHolder="Select model"
                    addPaylod={{ isGyroRequest: 0 }}
                  />
                </Form.Item>
              </Col>
            )}
            {/* <Col span={24}>
              <Form.Item
                name="custMachineModelName"
                label="Customer Machine Model"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator(
                      "enter machine serial number"
                    ),
                  },
                ]}
              >
                <Input placeholder="Enter customer machine model" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="custMachineModelDesc"
                label="Customer Machine Description"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator(
                      "enter machine serial number"
                    ),
                  },
                ]}
              >
                <Input placeholder="Enter customer machine description" />
              </Form.Item>
            </Col> */}
            <Col span={24}>
              <Form.Item
                name="amcRateYearly"
                label="AMC Rate (yearly)"
                rules={[
                  {
                    required: true,
                    message: "Please enter AMC Rate",
                  },
                ]}
              >
                <InputNumber<number>
                  // defaultValue={0}
                  formatter={(value) =>
                    value
                      ? `₹ ${new Intl.NumberFormat("en-IN").format(Number(value))}`
                      : "₹ 0"
                  }
                  parser={(value) =>
                    value?.replace(/₹\s?|,/g, "") as unknown as number
                  }
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="installationRate"
                label="Installation Rate"
                rules={[
                  {
                    required: true,
                    message: "Please installation rate",
                  },
                ]}
              >
                <InputNumber<number>
                  // defaultValue={0}
                  formatter={(value) =>
                    value
                      ? `₹ ${new Intl.NumberFormat("en-IN").format(Number(value))}`
                      : "₹ 0"
                  }
                  parser={(value) =>
                    value?.replace(/₹\s?|,/g, "") as unknown as number
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              className="btnDimensions"
            >
              {props.id ? "Update" : "Save"}
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
